import type {CopilotChatReference} from './copilot-chat-types'

/** URLs supported by this pattern should also be supported by the chat-links API. */
const referencableGitHubUrlPattern =
  /^(?:http:\/\/github\.localhost|https:\/\/github\.com)\/(?<repo>[^/]+\/[^/]+)\/(?<type>issues|pull|discussions)\/(?<id>[^/]+)/

const referenceMentionPattern = /^@(?<repo>[\w-.]+\/[\w-.]+)(?:\/(?<type>\w+)\/(?<id>.+))?$/

export type ReferenceType = 'issue' | 'pull-request' | 'discussion' | 'repository' | 'file'

const ReferenceType = {
  fromUrlType(urlType?: string) {
    switch (urlType) {
      case 'issues':
        return 'issue'
      case 'pull':
        return 'pull-request'
      case 'discussions':
        return 'discussion'
      case 'files':
        return 'file'
      case undefined:
        return 'repository'
    }
  },
}

export interface ReferenceMention {
  repo: string
  type: ReferenceType
  id?: string
}

export const ReferenceMention = {
  /**
   * Build the `@mention` syntax for the reference that corresponds to the URL. If the URL does not point to a
   * mentionable reference, returns `undefined`.
   */
  fromUrl(url: string): ReferenceMention | undefined {
    const match = referencableGitHubUrlPattern.exec(url)?.groups
    if (!match) return undefined

    const {repo = '', type, id} = match
    const referenceType = ReferenceType.fromUrlType(type)

    return referenceType ? {repo, type: referenceType, id} : undefined
  },
  stringify(mention: ReferenceMention) {
    switch (mention.type) {
      case 'issue':
        return `@${mention.repo}/issues/${mention.id ?? ''}`
      case 'pull-request':
        return `@${mention.repo}/pull/${mention.id ?? ''}`
      case 'discussion':
        return `@${mention.repo}/discussions/${mention.id ?? ''}`
      case 'repository':
        return `@${mention.repo}`
      case 'file':
        return `@${mention.repo}/files/${mention.id ?? ''}`
    }
  },
  parse(mention: string): ReferenceMention | undefined {
    const match = referenceMentionPattern.exec(mention)?.groups
    if (!match) return undefined

    const type = ReferenceType.fromUrlType(match.type)

    return type ? {repo: match.repo!, type, id: match.id} : undefined
  },
  refersTo(mention: ReferenceMention, reference: CopilotChatReference) {
    switch (mention.type) {
      case 'repository':
        return reference.type === 'repository' && `${reference.ownerLogin}/${reference.name}` === mention.repo
      case 'discussion':
        return (
          reference.type === 'discussion' &&
          `${reference.repository.owner}/${reference.repository.name}` === mention.repo &&
          reference.number.toString() === mention.id
        )
      case 'issue':
        return (
          reference.type === 'issue' &&
          `${reference.repository.owner}/${reference.repository.name}` === mention.repo &&
          reference.number.toString() === mention.id
        )
      case 'pull-request':
        return (
          reference.type === 'pull-request' &&
          `${reference.repository.ownerLogin}/${reference.repository.name}` === mention.repo &&
          reference.number.toString() === mention.id
        )
      case 'file':
        return (
          (reference.type === 'file' || reference.type === 'folder') &&
          `${reference.repoOwner}/${reference.repoName}` === mention.repo &&
          reference.path === mention.id
        )
      default:
        return false
    }
  },
}
